import React, { useEffect, useRef, useState } from "react";
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Dimensions,
  Platform,
} from "react-native";
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withTiming,
  withSequence,
  runOnJS,
  Easing,
} from "react-native-reanimated";
import { Ionicons } from "@expo/vector-icons";

const SCREEN_WIDTH = Dimensions.get("window").width;
const TOAST_DURATION = 4000;

export interface ToastNotificationData {
  id: string;
  title: string;
  message: string;
  type?: "info" | "success" | "warning" | "error";
}

interface ToastNotificationProps {
  notification: ToastNotificationData | null;
  onDismiss: () => void;
  onPress?: (notification: ToastNotificationData) => void;
}

const iconMap: Record<string, keyof typeof Ionicons.glyphMap> = {
  info: "information-circle",
  success: "checkmark-circle",
  warning: "warning",
  error: "alert-circle",
};

const colorMap: Record<string, string> = {
  info: "#3B82F6",
  success: "#10B981",
  warning: "#F59E0B",
  error: "#EF4444",
};

const ToastNotification: React.FC<ToastNotificationProps> = ({
  notification,
  onDismiss,
  onPress,
}) => {
  const translateY = useSharedValue(-100);
  const opacity = useSharedValue(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (notification) {
      // Slide in
      translateY.value = withTiming(0, {
        duration: 300,
        easing: Easing.out(Easing.cubic),
      });
      opacity.value = withTiming(1, { duration: 300 });

      // Auto-dismiss
      timerRef.current = setTimeout(() => {
        dismiss();
      }, TOAST_DURATION);
    }

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [notification]);

  const dismiss = () => {
    translateY.value = withTiming(-100, { duration: 250 }, () => {
      runOnJS(onDismiss)();
    });
    opacity.value = withTiming(0, { duration: 250 });
  };

  const animatedStyle = useAnimatedStyle(() => ({
    transform: [{ translateY: translateY.value }],
    opacity: opacity.value,
  }));

  if (!notification) return null;

  const type = notification.type || "info";

  return (
    <Animated.View
      style={[styles.container, animatedStyle]}
      pointerEvents="box-none"
    >
      <TouchableOpacity
        activeOpacity={0.9}
        onPress={() => {
          if (timerRef.current) clearTimeout(timerRef.current);
          onPress?.(notification);
          dismiss();
        }}
        style={[styles.toast, { borderLeftColor: colorMap[type] }]}
      >
        <View style={styles.iconContainer}>
          <Ionicons name={iconMap[type]} size={24} color={colorMap[type]} />
        </View>
        <View style={styles.textContainer}>
          <Text style={styles.title} numberOfLines={1}>
            {notification.title}
          </Text>
          <Text style={styles.message} numberOfLines={2}>
            {notification.message}
          </Text>
        </View>
        <TouchableOpacity onPress={dismiss} style={styles.closeButton}>
          <Ionicons name="close" size={18} color="#9CA3AF" />
        </TouchableOpacity>
      </TouchableOpacity>
    </Animated.View>
  );
};

const styles = StyleSheet.create({
  container: {
    position: "absolute",
    top: Platform.OS === "ios" ? 50 : 10,
    left: 12,
    right: 12,
    zIndex: 9999,
    elevation: 999,
  },
  toast: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "white",
    borderRadius: 12,
    borderLeftWidth: 4,
    paddingVertical: 12,
    paddingHorizontal: 14,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.15,
    shadowRadius: 8,
    elevation: 8,
  },
  iconContainer: {
    marginRight: 10,
  },
  textContainer: {
    flex: 1,
  },
  title: {
    fontSize: 14,
    fontWeight: "700",
    color: "#111827",
    marginBottom: 2,
  },
  message: {
    fontSize: 13,
    color: "#6B7280",
    lineHeight: 18,
  },
  closeButton: {
    padding: 4,
    marginLeft: 8,
  },
});

export default ToastNotification;
